import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import type { StepInitStatus } from 'components/layout';
import { isCreativeId, useLocationParams } from 'hooks';
import { getCreatives, queryKeys as creativesQueryKeys } from 'features/creatives';
import type { CreativeEntry } from 'types/shared';
import { Network } from '../../../constants';
import { getTwitterAccounts } from '../api/getAccounts';
import { getTwitterAdSettings } from '../api/getAdSettings';
import { getTwitterCampaigns } from '../api/getCampaigns';
import { getTwitterLineItems } from '../api/getLineItems';
import queryKeys from '../api/queryKeys';
import { resolveDeepLinkAdSettings } from '../utils/deepLinkAdSettings';
import { idsFromSelection } from '../utils/idSelection';
import { TWITTER_STEPS } from './twitterSteps';

interface TwitterDeepLinkInitializerProps {
  targetStep: number;
  onComplete: () => void;
  onReset: () => void;
  onStatusChange: (statuses: StepInitStatus[]) => void;
}

export const CREATIVES_FORMAT_MISMATCH = 'Creatives URL format is not valid for X Ads Automation';

const STEP_LABELS = TWITTER_STEPS.map((step) => step.title);

const TwitterDeepLinkInitializer = ({
  targetStep,
  onComplete,
  onReset,
  onStatusChange,
}: TwitterDeepLinkInitializerProps) => {
  const { watch, setValue } = useFormContext();
  const formApp = watch('app') as string | undefined;

  const {
    app: appParam,
    adAccount: adAccountParam,
    flatCreatives,
    creativesFormat,
    selectedCampaigns: selectedCampaignsParam,
    selectedAdSets: selectedAdSetsParam,
    adSettingsFlow: adSettingsFlowParam,
    resetLocationParamsToStart,
  } = useLocationParams();

  const resolvedApp = appParam ?? formApp ?? '';
  const creativesCompatible = creativesFormat !== 'pack';
  const urlCreativeIds = useMemo(
    () => (creativesCompatible ? [...new Set(flatCreatives)] : []),
    [creativesCompatible, flatCreatives],
  );
  const invalidCreativeIds = useMemo(
    () => urlCreativeIds.filter((id) => !isCreativeId(id)),
    [urlCreativeIds],
  );
  const validCreativeIds = useMemo(
    () => urlCreativeIds.filter((id) => isCreativeId(id)),
    [urlCreativeIds],
  );
  const needsCreativeCheck = creativesCompatible && urlCreativeIds.length > 0;
  const needsCreativeFetch = creativesCompatible && validCreativeIds.length > 0;

  const creativesRequestParams = useMemo(
    () => ({ app: resolvedApp, network: Network.X }),
    [resolvedApp],
  );

  const creativesQuery = useQuery({
    queryKey: creativesQueryKeys.creatives(creativesRequestParams),
    queryFn: ({ signal }) => getCreatives({ params: creativesRequestParams }, signal),
    enabled: needsCreativeFetch && !!resolvedApp,
    staleTime: 0,
  });

  const creativesMissing = useMemo(() => {
    const knownIds = creativesQuery.data ? new Set(creativesQuery.data.map((row) => row.id)) : undefined;
    const missingFromList = knownIds ? validCreativeIds.filter((id) => !knownIds.has(id)) : [];
    return [...invalidCreativeIds, ...missingFromList];
  }, [creativesQuery.data, invalidCreativeIds, validCreativeIds]);

  const step0Status: StepInitStatus = (() => {
    if (!creativesCompatible) return 'failed';
    if (!needsCreativeCheck) return 'success';
    if (!needsCreativeFetch) return creativesMissing.length > 0 ? 'failed' : 'success';
    if (creativesQuery.isPending) return 'loading';
    if (creativesQuery.isError) return 'failed';
    return creativesMissing.length > 0 ? 'failed' : 'success';
  })();
  const step0Pass = step0Status === 'success';

  const needsAccountCheck = !!adAccountParam && step0Pass;
  const campaignIds = useMemo(() => idsFromSelection(selectedCampaignsParam), [selectedCampaignsParam]);
  const lineItemIds = useMemo(() => idsFromSelection(selectedAdSetsParam), [selectedAdSetsParam]);
  const needsCampaignCheck = targetStep >= 2 && campaignIds.length > 0 && step0Pass;
  const needsLineItemCheck = targetStep >= 3 && lineItemIds.length > 0 && step0Pass;
  const needsAdSettingsCheck = targetStep >= 4 && step0Pass;

  const accountsQuery = useQuery({
    queryKey: [queryKeys.accounts, { app: resolvedApp }],
    queryFn: ({ signal }) => getTwitterAccounts({ params: { app: resolvedApp } }, signal),
    enabled: needsAccountCheck && !!resolvedApp,
    staleTime: 0,
  });

  const accountsMissing = useMemo<string[]>(() => {
    if (!adAccountParam || !accountsQuery.data) return [];
    return accountsQuery.data.some((account) => account.ad_account_id === adAccountParam)
      ? []
      : [adAccountParam];
  }, [accountsQuery.data, adAccountParam]);

  const step1Status: StepInitStatus = (() => {
    if (!needsAccountCheck) return adAccountParam && !step0Pass ? 'idle' : 'success';
    if (accountsQuery.isPending) return 'loading';
    if (accountsQuery.isError) return 'failed';
    return accountsMissing.length > 0 ? 'failed' : 'success';
  })();
  const step1Pass = step1Status === 'success';

  const campaignsQuery = useQuery({
    queryKey: [queryKeys.campaigns, adAccountParam],
    queryFn: ({ signal }) => getTwitterCampaigns({ params: { accountId: adAccountParam ?? '' } }, signal),
    enabled: needsCampaignCheck && step1Pass && !!adAccountParam,
    staleTime: 0,
  });

  const campaignsMissingIds = useMemo(() => {
    if (!campaignsQuery.data) return [];
    const knownIds = new Set(campaignsQuery.data.map((row) => row.campaign_id));
    return campaignIds.filter((id) => !knownIds.has(id));
  }, [campaignIds, campaignsQuery.data]);

  const step2Status: StepInitStatus = (() => {
    if (!needsCampaignCheck || !step1Pass) return 'idle';
    if (campaignsQuery.isPending) return 'loading';
    if (campaignsQuery.isError) return 'failed';
    return campaignsMissingIds.length > 0 ? 'failed' : 'success';
  })();
  const step2Pass = !needsCampaignCheck || step2Status === 'success';

  const lineItemsQuery = useQuery({
    queryKey: [queryKeys.lineItems, adAccountParam, campaignIds],
    queryFn: ({ signal }) =>
      getTwitterLineItems(
        { params: { accountId: adAccountParam ?? '', campaignIds } },
        signal,
      ),
    enabled: needsLineItemCheck && step2Pass && campaignIds.length > 0 && !!adAccountParam,
    staleTime: 0,
  });

  const selectableLineItems = useMemo(
    () =>
      (lineItemsQuery.data ?? []).flatMap((campaign) =>
        campaign.line_items.filter((lineItem) => !!lineItem.flow),
      ),
    [lineItemsQuery.data],
  );

  const lineItemsMissingIds = useMemo(() => {
    if (!lineItemsQuery.data) return [];
    const knownIds = new Set(selectableLineItems.map((lineItem) => lineItem.line_item_id));
    return lineItemIds.filter((id) => !knownIds.has(id));
  }, [lineItemIds, lineItemsQuery.data, selectableLineItems]);

  const step3Status: StepInitStatus = (() => {
    if (!needsLineItemCheck || !step2Pass) return 'idle';
    if (campaignIds.length === 0) return 'success';
    if (lineItemsQuery.isPending) return 'loading';
    if (lineItemsQuery.isError) return 'failed';
    return lineItemsMissingIds.length > 0 ? 'failed' : 'success';
  })();
  const step3Pass = !needsLineItemCheck || step3Status === 'success';

  const selectedFlows = useMemo(() => {
    const selected = new Set(lineItemIds);
    return [
      ...new Set(
        selectableLineItems
          .filter((lineItem) => selected.has(lineItem.line_item_id))
          .map((lineItem) => lineItem.flow!),
      ),
    ].sort();
  }, [lineItemIds, selectableLineItems]);

  const adSettingsQuery = useQuery({
    queryKey: [queryKeys.adSettings, resolvedApp, selectedFlows],
    queryFn: ({ signal }) =>
      getTwitterAdSettings({ params: { app: resolvedApp, flows: selectedFlows } }, signal),
    enabled: needsAdSettingsCheck && step3Pass && selectedFlows.length > 0 && !!resolvedApp,
    staleTime: 0,
  });

  const step4Status: StepInitStatus = (() => {
    if (!needsAdSettingsCheck || !step3Pass) return 'idle';
    if (selectedFlows.length === 0) return 'success';
    if (adSettingsQuery.isPending) return 'loading';
    if (adSettingsQuery.isError) return 'failed';
    return 'success';
  })();
  const step4Pass = !needsAdSettingsCheck || step4Status === 'success';

  const allPass = step0Pass && step1Pass && step2Pass && step3Pass && step4Pass;
  const statusArray = useMemo<StepInitStatus[]>(
    () => [step0Status, step1Status, step2Status, step3Status, step4Status],
    [step0Status, step1Status, step2Status, step3Status, step4Status],
  );
  const anyFailed = statusArray.includes('failed');

  useEffect(() => {
    onStatusChange(statusArray);
  }, [statusArray, onStatusChange]);

  const hasCompleted = useRef(false);

  useEffect(() => {
    if (!allPass || hasCompleted.current) return;
    hasCompleted.current = true;

    if (needsCreativeCheck && creativesQuery.data) {
      const selected = new Set(validCreativeIds);
      const rows = creativesQuery.data.filter((row: CreativeEntry) => selected.has(row.id));
      setValue('selectedCreatives', rows.map((row) => row.id));
      setValue('$creativesRows', rows);
    }

    if (needsCampaignCheck) {
      setValue('selectedCampaigns', campaignIds.filter((id) => !campaignsMissingIds.includes(id)));
    }

    if (needsLineItemCheck) {
      setValue('selectedLineItems', lineItemIds.filter((id) => !lineItemsMissingIds.includes(id)));
      if (lineItemsQuery.data) setValue('$lineItemsData', lineItemsQuery.data);
    }

    if (needsAdSettingsCheck && adSettingsQuery.data) {
      setValue('adSettings', resolveDeepLinkAdSettings(adSettingsFlowParam, adSettingsQuery.data));
      setValue('$adSettingsData', adSettingsQuery.data);
    }

    onComplete();
  }, [
    allPass,
    needsCreativeCheck,
    creativesQuery.data,
    validCreativeIds,
    needsCampaignCheck,
    campaignIds,
    campaignsMissingIds,
    needsLineItemCheck,
    lineItemIds,
    lineItemsMissingIds,
    lineItemsQuery.data,
    needsAdSettingsCheck,
    adSettingsQuery.data,
    adSettingsFlowParam,
    setValue,
    onComplete,
  ]);

  const failedStepMessages = useMemo(() => {
    const messages: { label: string; text: string }[] = [];

    if (step0Status === 'failed') {
      messages.push({
        label: STEP_LABELS[0],
        text: !creativesCompatible
          ? CREATIVES_FORMAT_MISMATCH
          : creativesMissing.length > 0
            ? `Not found: ${creativesMissing.join(', ')}`
            : 'Failed to load data',
      });
    }

    const rest: [StepInitStatus, string[], string][] = [
      [step1Status, accountsMissing, STEP_LABELS[1]],
      [step2Status, campaignsMissingIds, STEP_LABELS[2]],
      [step3Status, lineItemsMissingIds, STEP_LABELS[3]],
      [step4Status, [], STEP_LABELS[4]],
    ];
    rest.forEach(([status, missingIds, label]) => {
      if (status !== 'failed') return;
      messages.push({
        label,
        text: missingIds.length > 0 ? `Not found: ${missingIds.join(', ')}` : 'Failed to load data',
      });
    });

    return messages;
  }, [
    step0Status,
    creativesCompatible,
    creativesMissing,
    step1Status,
    accountsMissing,
    step2Status,
    campaignsMissingIds,
    step3Status,
    lineItemsMissingIds,
    step4Status,
  ]);

  const handleStartFromBeginning = useCallback(() => {
    resetLocationParamsToStart();
    onReset();
  }, [onReset, resetLocationParamsToStart]);

  if (anyFailed) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            width: '100%',
            maxWidth: 600,
            textAlign: 'center',
          }}
        >
          {failedStepMessages.map(({ label, text }) => (
            <Typography key={label} color="error.main" variant="body1" sx={{ lineHeight: 3 / 2 }}>
              {label}: {text}
            </Typography>
          ))}
          <Button variant="outlined" color="error" size="small" onClick={handleStartFromBeginning}>
            Start from beginning
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
      <CircularProgress />
    </Box>
  );
};

export default TwitterDeepLinkInitializer;
